import { FC, useMemo } from "react";

import { StudentOutcomeScoreTable } from "./StudentOutcomeScoreTable";
import { StudentScoreOverviewTable } from "./StudentScoreOverviewTable";
import { useFetchAndSelectMyAssessments, useFetchAssessmentData } from "./assessment-data";
import { mapStudentAssessmentScoreTableRow } from "./utils";

import { serverGetStudentAssessmentScoreTable } from "api/api-server-assessment";
import { ActivityType } from "components/server-types";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { AssessPointsMarkerIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  activitySelectors,
  pageOutcomeSelectors,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectFullSectionOutcomes,
  selectPagesWithAssessmentsForUserId,
  userSelectors,
} from "store/selectors";
import { useListSelector, usePromiseEffect } from "store/store-hooks";

type StudentPerformanceDashboardProps = {
  userId: string | null;
};

export const StudentPerformanceDashboard: FC<StudentPerformanceDashboardProps> = ({ userId }) => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const sectionOutcomes = useAppSelector(selectFullSectionOutcomes);

  const pageOutcomes = useAppSelector(pageOutcomeSelectors.selectAll);
  const activities = useListSelector(activitySelectors.selectAll);
  const studentUser = useAppSelector((s) => userSelectors.selectById(s, userId));
  const isViewingOwnAssessments = currentUserId === userId;

  useFetchAssessmentData();
  useFetchAndSelectMyAssessments();

  const {
    data: studentAssessmentScoreTableRows,
    isLoading: isLoadingScoreTable,
    error: scoreTableError,
  } = usePromiseEffect(async () => {
    if (!sectionId) {
      return null;
    }

    return serverGetStudentAssessmentScoreTable(sectionId, isViewingOwnAssessments ? null : userId);
  }, [sectionId, userId, isViewingOwnAssessments]);

  const scoreOverviewTableRows = useMemo(() => {
    if (!studentAssessmentScoreTableRows || scoreTableError) {
      return [];
    }

    return studentAssessmentScoreTableRows.map(mapStudentAssessmentScoreTableRow);
  }, [studentAssessmentScoreTableRows, scoreTableError]);

  const activitiesById = useMemo(
    () =>
      activities.reduce(
        (acc, activity) => ({
          ...acc,
          [activity.id]: activity,
        }),
        {} as Record<string, ActivityType>,
      ),
    [activities],
  );

  const pagesWithAssessments = useAppSelector((s) =>
    selectPagesWithAssessmentsForUserId(s, userId, sectionId),
  );
  const allAssessments = pagesWithAssessments.map((p) => p.assessments).flat();
  const allOutcomeAssessments = allAssessments.map((a) => a.outcome_assessments || []).flat();

  const hasNoScoreData = sectionId && !isLoadingScoreTable && scoreOverviewTableRows.length === 0;

  if (hasNoScoreData) {
    return (
      <EmptyCard
        className="assessment-dashboard mb-4"
        description={t(
          isViewingOwnAssessments
            ? "assessment_dashboard.all.empty_description"
            : "assessment_dashboard.all.empty_description_instructor",
          {
            studentName: studentUser?.first_name || t("assessment_dashboard.all.unknown_student"),
          },
        )}
        icon={<AssessPointsMarkerIcon className="text-blue" />}
        title={t(
          isViewingOwnAssessments
            ? "assessment_dashboard.all.empty_title"
            : "assessment_dashboard.all.empty_title_instructor",
        )}
      />
    );
  }

  if (isLoadingScoreTable || !sectionId) {
    return null;
  }

  return (
    <main className="assessment-dashboard overflow-hidden">
      <StudentScoreOverviewTable sectionId={sectionId} tableRows={scoreOverviewTableRows} />

      <StudentOutcomeScoreTable
        activitiesById={activitiesById}
        allOutcomeAssessments={allOutcomeAssessments}
        pageOutcomes={pageOutcomes}
        pagesWithAssessments={pagesWithAssessments}
        sectionOutcomes={sectionOutcomes}
      />
    </main>
  );
};
